// frontend/src/pages/Templates/TemplateList.jsx
import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../hooks/useAuth";
import { getTemplates, createTemplate } from "../../api/template.api.js";
import {
  PageHeader,
  Spinner,
  EmptyState,
  formatDate,
  formatFileSize,
} from "../../components/ui";
import {
  BookOpen,
  Plus,
  X,
  Upload,
  FileText,
  Users,
  ChevronRight,
} from "lucide-react";

const CATEGORIES = ["NDA", "MOU", "PKS", "CORPORATE_ACTION", "ADDENDUM", "GENERAL"];
const AUDIENCES = ["INTERNAL", "VENDOR", "PARTNER"];

const CATEGORY_COLORS = {
  NDA: "bg-purple-50 text-purple-700 border-purple-200",
  MOU: "bg-blue-50 text-blue-700 border-blue-200",
  PKS: "bg-green-50 text-green-700 border-green-200",
  CORPORATE_ACTION: "bg-teal-50 text-teal-700 border-teal-200",
  ADDENDUM: "bg-orange-50 text-orange-700 border-orange-200",
  GENERAL: "bg-gray-50 text-gray-600 border-gray-200",
};

const CATEGORY_LABELS = {
  NDA: "NDA",
  MOU: "MOU",
  PKS: "PKS",
  CORPORATE_ACTION: "Corporate Action",
  ADDENDUM: "Addendum",
  GENERAL: "General",
};

function catLabel(cat) {
  return CATEGORY_LABELS[cat] ?? cat;
}

function catColor(cat) {
  return CATEGORY_COLORS[cat] ?? "bg-indigo-50 text-indigo-700 border-indigo-200";
}

const AUDIENCE_LABELS = { INTERNAL: "Internal", VENDOR: "Vendor", PARTNER: "Partner" };

// ─── New template modal ────────────────────────────────────────────────────────

function NewTemplateModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "",
    audience: [],
    versionLabel: "",
    notes: "",
  });
  const [file, setFile] = useState(null);
  const [categoryIsCustom, setCategoryIsCustom] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef();

  function toggleAudience(a) {
    setForm((f) => ({
      ...f,
      audience: f.audience.includes(a)
        ? f.audience.filter((x) => x !== a)
        : [...f.audience, a],
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    if (!form.title.trim()) return setError("Title is required");
    if (!form.category) return setError("Category is required");
    if (form.audience.length === 0) return setError("Select at least one audience");
    if (!file) return setError("File is required");

    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", form.title.trim());
    fd.append("description", form.description.trim());
    fd.append("category", form.category);
    fd.append("audience", JSON.stringify(form.audience));
    fd.append("versionLabel", form.versionLabel.trim() || "v1");
    fd.append("notes", form.notes.trim());

    setSubmitting(true);
    try {
      const created = await createTemplate(fd);
      onSuccess(created);
    } catch (err) {
      setError(err.message || "Upload failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">New Template</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              className="input text-sm"
              placeholder="e.g. NDA Template Standar"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Description</label>
            <textarea
              className="input text-sm resize-none"
              rows={2}
              placeholder="Brief description of this template..."
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                className="input text-sm"
                value={categoryIsCustom ? "_custom" : form.category}
                onChange={(e) => {
                  if (e.target.value === "_custom") {
                    setCategoryIsCustom(true);
                    setForm((f) => ({ ...f, category: "" }));
                  } else {
                    setCategoryIsCustom(false);
                    setForm((f) => ({ ...f, category: e.target.value }));
                  }
                }}
              >
                <option value="">Select category</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{catLabel(c)}</option>
                ))}
                <option value="_custom">Lainnya...</option>
              </select>
              {categoryIsCustom && (
                <input
                  className="input text-sm mt-2"
                  placeholder="Nama kategori..."
                  value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  autoFocus
                />
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Version label</label>
              <input
                className="input text-sm"
                placeholder="v1"
                value={form.versionLabel}
                onChange={(e) => setForm((f) => ({ ...f, versionLabel: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              Audience <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2 flex-wrap">
              {AUDIENCES.map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => toggleAudience(a)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                    form.audience.includes(a)
                      ? "bg-brand-600 text-white border-brand-600"
                      : "bg-white text-gray-600 border-gray-200 hover:border-brand-400"
                  }`}
                >
                  {AUDIENCE_LABELS[a]}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              File <span className="text-red-500">*</span>
              <span className="ml-1 text-gray-400 font-normal">(PDF or DOCX)</span>
            </label>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              className="hidden"
              onChange={(e) => setFile(e.target.files[0] || null)}
            />
            {file ? (
              <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50">
                <FileText size={16} className="text-gray-400 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-800 truncate">{file.name}</p>
                  <p className="text-xs text-gray-400">{formatFileSize(file.size)}</p>
                </div>
                <button
                  type="button"
                  onClick={() => { setFile(null); fileRef.current.value = ""; }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current.click()}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-dashed border-gray-300 text-sm text-gray-500 hover:border-brand-400 hover:text-brand-600 transition-colors"
              >
                <Upload size={15} />
                Choose file
              </button>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">Version notes</label>
            <input
              className="input text-sm"
              placeholder="What's in this version? (optional)"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1 text-sm">
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary flex-1 text-sm flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  Uploading...
                </>
              ) : (
                "Create template"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Template card ─────────────────────────────────────────────────────────────

function TemplateCard({ template }) {
  const latest = template.versions?.[0];
  const versionCount = template._count?.versions ?? 0;

  return (
    <Link
      to={`/templates/${template.id}`}
      className="card p-5 hover:shadow-md transition-all duration-200 flex flex-col gap-3 group"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 group-hover:text-brand-700 transition-colors leading-snug">
            {template.title}
          </h3>
          {template.description && (
            <p className="text-xs text-gray-500 mt-1 line-clamp-2">{template.description}</p>
          )}
        </div>
        <span
          className={`flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded border ${catColor(template.category)}`}
        >
          {catLabel(template.category)}
        </span>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        {template.audience.map((a) => (
          <span key={a} className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
            <Users size={10} />
            {AUDIENCE_LABELS[a]}
          </span>
        ))}
      </div>

      <div className="flex items-center justify-between pt-1 border-t border-gray-100">
        <div className="text-xs text-gray-400">
          {versionCount} version{versionCount !== 1 ? "s" : ""}
          {latest && (
            <span className="ml-2 text-gray-300">·</span>
          )}
          {latest && (
            <span className="ml-2">{latest.versionLabel} · {formatDate(latest.uploadedAt)}</span>
          )}
        </div>
        <ChevronRight size={14} className="text-gray-300 group-hover:text-brand-400 transition-colors" />
      </div>
    </Link>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

export default function TemplateList() {
  const { isMaster } = useAuth();
  const qc = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ["templates", categoryFilter, includeInactive],
    queryFn: () =>
      getTemplates({
        ...(categoryFilter && { category: categoryFilter }),
        ...(includeInactive && { includeInactive: "true" }),
      }),
  });

  function handleCreated(template) {
    setShowModal(false);
    qc.invalidateQueries({ queryKey: ["templates"] });
  }

  return (
    <div>
      <PageHeader
        title="Template Dokumen"
        subtitle={`${templates.length} template${templates.length !== 1 ? "s" : ""}`}
        action={
          isMaster && (
            <button
              onClick={() => setShowModal(true)}
              className="btn-primary flex items-center gap-2 text-sm"
            >
              <Plus size={15} />
              New Template
            </button>
          )
        }
      />

      {/* ── Filter bar ────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="flex gap-1.5 flex-wrap">
          <button
            onClick={() => setCategoryFilter("")}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              !categoryFilter
                ? "bg-brand-600 text-white"
                : "bg-white border border-gray-200 text-gray-600 hover:border-brand-400"
            }`}
          >
            All
          </button>
          {[...CATEGORIES, ...[...new Set(templates.map((t) => t.category).filter((c) => c && !CATEGORIES.includes(c)))]]
            .map((c) => (
            <button
              key={c}
              onClick={() => setCategoryFilter(c === categoryFilter ? "" : c)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                categoryFilter === c
                  ? "bg-brand-600 text-white"
                  : "bg-white border border-gray-200 text-gray-600 hover:border-brand-400"
              }`}
            >
              {catLabel(c)}
            </button>
          ))}
        </div>

        {isMaster && (
          <label className="ml-auto flex items-center gap-2 text-xs text-gray-500 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="rounded border-gray-300"
            />
            Include inactive
          </label>
        )}
      </div>

      {/* ── Template grid ─────────────────────────────────────────────────── */}
      {isLoading ? (
        <Spinner />
      ) : templates.length === 0 ? (
        <EmptyState
          title="No templates yet"
          description={
            categoryFilter
              ? `No templates in category "${categoryFilter}".`
              : "Upload your first document template."
          }
          action={
            isMaster && (
              <button
                onClick={() => setShowModal(true)}
                className="btn-primary text-sm flex items-center gap-1.5"
              >
                <Plus size={14} /> New Template
              </button>
            )
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {templates.map((t) => (
            <TemplateCard key={t.id} template={t} />
          ))}
        </div>
      )}

      {showModal && (
        <NewTemplateModal
          onClose={() => setShowModal(false)}
          onSuccess={handleCreated}
        />
      )}
    </div>
  );
}
